// ProjectBanner — the top strip above the Navbar carrying project-mode signals.
// Two orthogonal, composable signals (a project can be both):
//   - Demo  (`type === 'demo'`): a sample-data sandbox indicator (no countdown;
//            the full demo experience — fake data/streams — lands later).
//   - Trial (`is_trial` + `trial_expires_at`): "X days left · Contact us".
//            Once past expiry the backend gate (TRIAL_EXPIRED) redirects to
//            /project-blocked, so the countdown only shows during the trial.
//
// Renders nothing when neither applies. Reads the current project via useProject
// (null off-project). "Contact us" is a placeholder (real contacts later) —
// it toasts the shared "coming soon".

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOptionalProjectParams } from '../../hooks/useProjectParams.js';
import { useProject } from '../../hooks/useProject.js';
import { trialDaysLeft } from '../../lib/projectAccess.js';
import { useToast } from '../ui/Toast.js';
import { Button } from '../ui/Button.js';
import { Banner } from '../ui/Banner.js';
import { pageBox, pageGutter } from './pageBox.js';

export function ProjectBanner() {
  const { t } = useTranslation('nav');
  const projectId = useOptionalProjectParams();
  const { data: project } = useProject(projectId ?? '');
  const toast = useToast();
  // Capture "now" once at mount (a lazy initializer — the pure place to read an
  // impure clock); the days-left figure is mount-stable, fine for a trial banner.
  const [now] = useState(() => Date.now());

  if (!project) return null;
  const isDemo = project.type === 'demo';
  const isTrial = project.is_trial && project.trial_expires_at !== null;
  if (!isDemo && !isTrial) return null;

  // ≤ 0 = already expired (a sysadmin can still be here via the gate bypass).
  const days = isTrial ? trialDaysLeft(project.trial_expires_at!, now) : 0;
  const daysText = days > 0 ? t('banner.trial.daysLeft', { count: days }) : t('banner.trial.expired');

  // The PROJECT-scope band: a neutral, centered Banner. Caps to the shared
  // pageBox (boxed app frame) like the Navbar band; the gutter rides the Banner
  // content (className) so the fill spans the measure with calm canvas wings.
  return (
    <div className={pageBox}>
      <Banner variant="neutral" align="center" className={pageGutter}>
        {isDemo && (
          <span className="font-semibold text-[var(--color-text-primary)]">{t('banner.demo')}</span>
        )}
        {isDemo && isTrial && <span aria-hidden>·</span>}
        {isTrial && (
          <>
            <span>{daysText}</span>
            <span aria-hidden>·</span>
            {/* The negative margin grows the hit target (WCAG 2.5.8) without growing the strip. */}
            <Button variant="link" className="-my-1 py-1" onClick={() => toast.info(t('tools.comingSoon'))}>
              {t('banner.trial.contactUs')}
            </Button>
          </>
        )}
      </Banner>
    </div>
  );
}
